import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2, Sparkles } from "lucide-react";

export default function Pricing() {
  const [tiers, setTiers] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [founderOffer, setFounderOffer] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.tiers().then((d) => setTiers(d.tiers)).catch(() => toast.error("Erreur chargement tarifs"));
    api.founderOffer().then(setFounderOffer).catch(() => {});
  }, []);

  const subscribe = async (tierId, billingCycle = cycle) => {
    if (tierId === "free") {
      navigate("/app");
      return;
    }
    setCheckoutLoading(tierId);
    try {
      const origin = window.location.origin;
      const res = await api.createCheckout(tierId, origin, billingCycle);
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        toast.error("URL de checkout manquante");
      }
    } catch (e) {
      toast.error("Erreur checkout : " + (e.response?.data?.detail || e.message));
    } finally {
      setCheckoutLoading(null);
    }
  };

  const order = ["free", "signals", "institutional"];
  const badges = { signals: "Populaire", institutional: "Complet" };
  const highlighted = "signals";

  return (
    <div className="min-h-screen bg-bg text-white" data-testid="pricing-page">
      {/* NAV */}
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-accent-blue to-blue-600 flex items-center justify-center font-bold text-lg">A</div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Analyse Statistique</div>
            </div>
          </Link>
          <Link to="/app" className="btn" data-testid="pricing-app-link">Dashboard</Link>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-blue/10 border border-accent-blue/30 rounded-full text-xs uppercase tracking-widest text-accent-blue mb-4">
            <Sparkles size={12} /> Tarifs transparents · Résiliation en 1 clic
          </div>
          <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">Choisis ton niveau</h1>
          <p className="mt-4 text-white/60 max-w-2xl mx-auto">
            Des tarifs alignés sur le marché — la rigueur statistique en plus. Passe au tier supérieur quand tu veux.
          </p>
          <div className="mt-6 inline-flex items-center gap-1 p-1 rounded-full border border-white/10 bg-white/5" data-testid="billing-toggle">
            <button
              onClick={() => setCycle("monthly")}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${cycle === "monthly" ? "bg-accent-blue text-black" : "text-white/60 hover:text-white"}`}
              data-testid="billing-monthly"
            >Mensuel</button>
            <button
              onClick={() => setCycle("yearly")}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${cycle === "yearly" ? "bg-accent-blue text-black" : "text-white/60 hover:text-white"}`}
              data-testid="billing-yearly"
            >Annuel <span className="text-[10px] opacity-80">(2 mois offerts)</span></button>
          </div>
        </div>

        {founderOffer && !founderOffer.sold_out && (
          <div className="mb-10 panel p-5 border-warn/40 bg-warn/5 flex flex-wrap items-center justify-between gap-4" data-testid="founder-offer-banner">
            <div>
              <div className="font-heading font-bold text-lg flex items-center gap-2">
                🚀 Offre Fondateur — Expert à VIE pour {founderOffer.price_once} €
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-warn/20 text-warn border border-warn/40">
                  {founderOffer.remaining} places restantes / {founderOffer.seats}
                </span>
              </div>
              <div className="text-xs text-white/55 mt-1">
                Paiement unique. Accès Expert illimité, pour toujours — réservé aux {founderOffer.seats} premiers membres.
              </div>
            </div>
            <button
              onClick={() => subscribe("founder_lifetime", "once")}
              disabled={checkoutLoading === "founder_lifetime"}
              className="btn btn-primary"
              data-testid="founder-offer-cta"
            >
              {checkoutLoading === "founder_lifetime" ? <Loader2 size={14} className="animate-spin" /> : "Devenir membre fondateur"} <ArrowRight size={14} />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-testid="pricing-tiers">
          {tiers && order.map((tid) => {
            const t = tiers[tid];
            if (!t) return null;
            const isHighlighted = tid === highlighted;
            const price = cycle === "yearly" ? t.price_yearly : t.price_monthly;
            return (
              <div
                key={tid}
                className={`panel p-6 flex flex-col relative ${isHighlighted ? "border-accent-blue/60 shadow-xl shadow-accent-blue/10" : ""}`}
                data-testid={`tier-card-${tid}`}
              >
                {badges[tid] && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold ${
                    isHighlighted ? "bg-accent-blue text-black" : "bg-white/10 text-white/70"
                  }`}>{badges[tid]}</div>
                )}
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-widest text-white/50">{t.name}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-heading text-4xl font-bold">{price} €</span>
                    <span className="text-white/50 text-sm">{cycle === "yearly" ? "/an" : "/mois"}</span>
                  </div>
                  {cycle === "yearly" && t.price_monthly > 0 && (
                    <div className="text-[10px] text-up mt-1">soit {(t.price_yearly / 12).toFixed(2).replace(".", ",")} €/mois — 2 mois offerts</div>
                  )}
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                      <Check size={14} className="text-up mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => subscribe(tid)}
                  disabled={checkoutLoading === tid}
                  className={`btn ${isHighlighted ? "btn-primary" : ""} w-full justify-center`}
                  data-testid={`tier-cta-${tid}`}
                >
                  {checkoutLoading === tid ? <Loader2 size={14} className="animate-spin" /> :
                    tid === "free" ? "Commencer" : "S'abonner"} <ArrowRight size={14} />
                </button>
              </div>
            );
          })}
        </div>

        <div className="text-center text-xs text-white/40 mt-12 max-w-2xl mx-auto" data-testid="pricing-disclaimer">
          Paiements en euros (€), mensuels ou annuels. TVA/taxes locales calculées à la caisse.
          Résiliation depuis le portail client Stripe. Aucun frais caché.
        </div>
      </section>
    </div>
  );
}
